import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { UserListItem } from '../../types/user';

/**
 * Props for UserCard component
 */
interface UserCardProps {
  user: UserListItem;
  onApprove?: (userId: string) => void;
  onReject?: (userId: string) => void;
  onEdit?: (userId: string) => void;
  showActions?: boolean;
  compact?: boolean;
}

/**
 * UserCard Component
 * Displays user summary card with avatar, role, status, and quick actions
 *
 * Features:
 * - User avatar with initials
 * - Display name and email
 * - Color-coded role badges
 * - Status indicators (verified, approved, suspended)
 * - Quick approve/reject buttons for pending users
 * - Navigation to user detail page
 * - Last login timestamp
 */
const UserCard: React.FC<UserCardProps> = ({
  user,
  onApprove,
  onReject,
  onEdit,
  showActions = true,
  compact = false,
}) => {
  const navigate = useNavigate();

  /**
   * Get user initials for avatar
   */
  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Get role badge color
   */
  const getRoleColor = (
    role: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'editor':
        return 'warning';
      case 'contributor':
        return 'info';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  /**
   * Get user status based on isApproved
   */
  const getUserStatus = (): string => {
    if (!user.isApproved) return 'pending';
    return 'approved';
  };

  /**
   * Get status color
   */
  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  /**
   * Handle card click to navigate to user detail
   */
  const handleCardClick = () => {
    navigate(`/admin/users/${user.uid}`);
  };

  /**
   * Handle approve button click
   */
  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onApprove) {
      onApprove(user.uid);
    }
  };

  /**
   * Handle reject button click
   */
  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onReject) {
      onReject(user.uid);
    }
  };

  /**
   * Handle edit button click
   */
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onEdit) {
      onEdit(user.uid);
    } else {
      navigate(`/admin/users/${user.uid}`);
    }
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* User Avatar and Basic Info */}
        <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
          <Avatar
            sx={{
              width: compact ? 48 : 64,
              height: compact ? 48 : 64,
              bgcolor: `${getRoleColor(user.role)}.main`,
              fontSize: compact ? '1rem' : '1.5rem',
            }}
          >
            {getInitials(user.displayName)}
          </Avatar>

          <Box flexGrow={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant={compact ? 'body1' : 'h6'} fontWeight={600}>
                {user.displayName}
              </Typography>
              {user.isEmailVerified && (
                <Tooltip title="Email verified">
                  <VerifiedIcon fontSize="small" color="primary" />
                </Tooltip>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" noWrap>
              {user.email}
            </Typography>

            {/* Role and Status Badges */}
            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
              <Chip
                label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                size="small"
                color={getRoleColor(user.role)}
              />
              <Chip
                label={getUserStatus().charAt(0).toUpperCase() + getUserStatus().slice(1)}
                size="small"
                color={getStatusColor(getUserStatus())}
              />
            </Box>
          </Box>
        </Box>

        {/* Additional Information */}
        {!compact && (
          <Box mt={2} pt={2} borderTop={1} borderColor="divider">
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="caption" color="text.secondary">
                Member since:
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {user.createdAt ? format(user.createdAt, 'MMM d, yyyy') : 'Unknown'}
              </Typography>
            </Box>

            {user.lastLoginAt && (
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="caption" color="text.secondary">
                  Last login:
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {format(user.lastLoginAt, 'MMM d, yyyy h:mm a')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      {/* Action Buttons */}
      {showActions && (
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          {getUserStatus() === 'pending' ? (
            <>
              {onApprove && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleApprove}
                >
                  Approve
                </Button>
              )}
              {onReject && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleReject}
                >
                  Reject
                </Button>
              )}
            </>
          ) : (
            <Tooltip title="View details">
              <IconButton size="small" onClick={handleEdit}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </CardActions>
      )}
    </Card>
  );
};

export default UserCard;
